/*
 * Copyright (c) Nexign, JSC, 2019.
 */
/**
 *
 * @author Evgenij Tyan
 * @author Yakov Gushchin
 * @version 1.1.0
 * @since 1.0.0
 */
import { Fragment, RefObject, useRef } from 'react';

import { FieldProps } from 'formik';
import invoke from 'lodash/invoke';
import moment, { Moment } from 'moment';

import { icons } from '@assets/index';
import { DropDownFilter, RangeDatePicker } from '@components/common';
import { EInputKind } from '@components/common/Input/Input';
import { LabelStyled } from '@components/common/Input/styled/InputStyled';
import { ICommonTranslation } from '@modules/internationalization/types';

import { ETopicAttrVisibility } from '../../types/createInquiryTypes';
import {
    ErrorMessage,
    FieldRequiredStyled,
    FormItemContainerStyled,
} from './styled/CreateInquiryFormStyled';

export interface ICreateInquiryFormDate {
    label: string;
    required: boolean;
    translation: ICommonTranslation;
    visibility: ETopicAttrVisibility;
}


export const CreateInquiryFormDate = ({
    field,
    form: { touched, errors, setFieldValue },
    ...props
}: FieldProps & ICreateInquiryFormDate) => {

    const calendarRef: RefObject<RangeDatePicker> = useRef<RangeDatePicker>(null);

    const calendarHandler = (leftRange: Moment) => {
        setFieldValue(field.name, leftRange.format('YYYY-MM-DDTHH:mm:ss.SSSSZ'));
    };
    const okButtonHandler = () => {
        invoke(calendarRef, 'current.onApply');
    };

    const filterValue = field.value ? moment(field.value).format('DD.MM.YYYY HH:mm') : '';
    const date = field.value ? moment(field.value) : moment();
    const timeLeftHour = parseInt(date.format('HH'));
    const timeLeftMin = parseInt(date.format('mm'));

    const isError = touched[field.name] && errors[field.name];
    const kind = isError ? EInputKind.error : EInputKind.normal;
    return (
        <FormItemContainerStyled>
            <LabelStyled htmlFor={field.name} kind={kind}>
                {props.label}
                {
                    props.required &&
                    <FieldRequiredStyled>*</FieldRequiredStyled>
                }
            </LabelStyled>
            {
                props.visibility !== ETopicAttrVisibility.READ_ONLY &&
                <Fragment>
                    <DropDownFilter
                        filterButtonLabel={filterValue}
                        icon={icons.calendarIcon}
                        kind={kind}
                        showCancelBtn={true}
                        translation={props.translation.dropDown}
                        widthAuto={true}
                        onOkClick={okButtonHandler}
                    >
                        <RangeDatePicker
                            endDate={date}
                            messages={props.translation.calendar}
                            range={date}
                            ref={calendarRef}
                            single={true}
                            startDate={date}
                            timeLeftHour={timeLeftHour}
                            timeLeftMin={timeLeftMin}
                            onApply={calendarHandler}
                        />
                    </DropDownFilter>
                    {
                        isError &&
                        <ErrorMessage>
                            {
                                errors[field.name]
                            }
                        </ErrorMessage>
                    }
                </Fragment>
            }
        </FormItemContainerStyled>
    );
};
